/**
 * The AI Service hub's Overview tab, with its own edit mode (task 784).
 *
 * Until now `ServiceDetailPage` rendered `<EndpointOverviewTab isEditing={false} />`
 * with the flag hard-coded, so the edit machinery that already existed was
 * unreachable from this hub: nothing about a service could be changed here
 * except its path.
 *
 * **Why a wrapper rather than an edit mode inside `EndpointOverviewTab`.** That
 * component is shared with the legacy endpoint hub (`/endpoints/:id`), whose own
 * page header owns a bulk edit mode and drives the very same `isEditing` /
 * `onFieldChange` props. Giving the shared component a second, self-managed edit
 * mode would put two controllers on one piece of state. Instead the state lives
 * here, the shared component stays purely presentational about editing, and the
 * legacy hub is untouched by construction.
 *
 * Save sends ONLY the fields that were actually touched — not a copy of the
 * whole endpoint — so a concurrent change to something this mode does not show
 * (a request or response schema, say) cannot be clobbered by pressing Save
 * here. That separation is what lets `ServiceSchemaCards` sit on this tab
 * (task 882) with its own per-card save: two edit surfaces, one page, no shared
 * state. Path edits run the same validate-then-normalize contract as the create
 * form and the inline path editor (task 388), and every failure resolves through
 * `describeEndpointSaveError` rather than being logged.
 *
 * The title is NOT edited here — `ServiceTitleEditor` in the page header owns
 * `name`, independently of this mode.
 *
 * **Danger Zone (task 1042).** The tab ends in the service's one destructive
 * action, Delete, moved here from the page header, where it sat beside Publish
 * on every tab. It is gated on `innovate.fe.services.delete` (the affordance
 * twin of the backend's `endpoints.manage`) and outside the edit mode, like the
 * schema cards. This tab owns the whole flow the header used to: the existing
 * `DeleteServiceDialog`, the mutation, the toast, and the navigation back to
 * the list once the page's subject is gone.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckIcon, PencilIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { Endpoint, UpdateEndpointInput } from '@/types';
import { useDeleteEndpoint, useUpdateEndpoint } from '@/hooks/useEndpoints';
import { useFlag } from '@/hooks/useFeatureFlags';
import { useToast } from '@/contexts/ToastContext';
import { refusalReason } from '@/utils/apiRefusal';
import { endpointDisplayName } from '@/utils/endpointLabel';
import { DeleteServiceDialog } from '@/components/services/DeleteServiceDialog';
import { EndpointOverviewTab } from '@/components/endpoints/EndpointOverviewTab';
import {
  normalizeEndpointPathValue,
  validateEndpointPath,
} from '@/components/endpoints/EndpointPathInput';
import { describeEndpointSaveError } from '@/components/endpoints/endpointSaveError';
import { ServiceSchemaCards } from './ServiceInputOutputTab';
import { ServiceStopHistoryCard } from './ServiceStopHistoryCard';

interface ServiceOverviewTabProps {
  endpoint: Endpoint;
}

export function ServiceOverviewTab({ endpoint }: ServiceOverviewTabProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [edited, setEdited] = useState<Partial<Endpoint>>({});
  const updateEndpoint = useUpdateEndpoint();
  const toast = useToast();
  const navigate = useNavigate();
  const canDelete = useFlag('innovate.fe.services.delete');
  const deleteEndpoint = useDeleteEndpoint();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // What the cards render while editing: the server's copy with the pending
  // changes laid over it, so an untouched field still shows its stored value.
  const shown = { ...endpoint, ...edited } as Endpoint;

  const handleFieldChange = (field: string, value: unknown) => {
    setEdited(previous => ({ ...previous, [field]: value }));
  };

  const cancel = () => {
    setIsEditing(false);
    setEdited({});
  };

  const save = async () => {
    // Nothing touched — closing is the whole job, and a PATCH with an empty
    // body would be a write with no meaning.
    if (Object.keys(edited).length === 0) {
      setIsEditing(false);
      return;
    }

    const data: UpdateEndpointInput = { ...edited } as UpdateEndpointInput;

    if ('path' in edited) {
      const pathValue = edited.path ?? '';
      const pathError = validateEndpointPath(pathValue);
      if (pathError) {
        toast.error(pathError);
        return;
      }
      data.path = normalizeEndpointPathValue(pathValue);
    }

    try {
      await updateEndpoint.mutateAsync({ id: endpoint.id, data });
      setIsEditing(false);
      setEdited({});
      toast.success('AI Service updated');
    } catch (error) {
      toast.error(describeEndpointSaveError(error, 'Failed to update this AI Service').message);
    }
  };

  const handleDelete = () => {
    deleteEndpoint.mutate(endpoint.id, {
      onSuccess: () => {
        setConfirmingDelete(false);
        toast.success(
          `Deleted ${endpointDisplayName(endpoint)}`,
          'You can restore it from Settings → Archive.',
          { label: 'View Archive', href: '/settings/archive' }
        );
        // The page's subject is gone; staying on it would render an error card.
        navigate('/services');
      },
      // The service's own refusal, never a guess (the task-858 rule).
      onError: (deleteError) =>
        toast.error('Could not delete this AI Service', refusalReason(deleteError)),
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2">
        {isEditing ? (
          <>
            <button
              type="button"
              onClick={save}
              disabled={updateEndpoint.isPending}
              data-testid="service-overview-save"
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              <CheckIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
              {updateEndpoint.isPending ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={cancel}
              disabled={updateEndpoint.isPending}
              data-testid="service-overview-cancel"
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              <XMarkIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
              Cancel
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            data-testid="service-overview-edit"
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <PencilIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
            Edit
          </button>
        )}
      </div>

      {/* `showSchemas={false}` suppresses the shared component's own read-only
          copies, so the editable cards below are the page's single pair. */}
      <EndpointOverviewTab
        endpoint={shown}
        isEditing={isEditing}
        onFieldChange={handleFieldChange}
        showSchemas={false}
      />

      {/* The contract, below the Request card and OUTSIDE this tab's edit mode
          (task 882): each card keeps the per-card read → edit → save cycle it
          had on the Input/Output tab, so pressing Edit here never puts a schema
          into an editing state, and saving a schema never writes a description. */}
      <ServiceSchemaCards endpoint={endpoint} />

      {/* Stop history (task 1164), above the Danger Zone: a stop is reversible,
          so its record sits with the service's state, not its destruction. */}
      <ServiceStopHistoryCard endpointId={endpoint.id} />

      {/* Danger Zone (task 1042): the tab's last section, and the only place on
          the page that deletes the service. */}
      {canDelete && (
        <section data-testid="service-danger-zone" className="pt-4">
          <h3 className="text-sm font-semibold text-red-600 dark:text-red-400">Danger Zone</h3>
          <div className="mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-red-300 dark:border-red-800 p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Deleting stops the service's URL answering at once and moves it to your account's
                Archive, where it can be restored.
              </p>
              <button
                type="button"
                data-testid="service-delete"
                onClick={() => setConfirmingDelete(true)}
                className="inline-flex shrink-0 items-center justify-center rounded-md border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 dark:border-red-700 dark:bg-gray-800 dark:text-red-400 dark:hover:bg-red-900/20"
              >
                <TrashIcon className="-ml-1 mr-2 h-4 w-4" />
                Delete AI Service
              </button>
            </div>
          </div>
        </section>
      )}

      {confirmingDelete && (
        <DeleteServiceDialog
          endpoint={endpoint}
          onClose={() => setConfirmingDelete(false)}
          onConfirm={handleDelete}
          isDeleting={deleteEndpoint.isPending}
        />
      )}
    </div>
  );
}

export default ServiceOverviewTab;
